// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO: pure helpers behind the connector editor: IDs from names, blank drafts, and filling in
// what the action rows leave implicit before a definition is sent to the API.

import type {
	AssistantConnectorDefinition,
	ConnectorCapability,
	ConnectorParameter,
} from '@konvo/schema/src/domains/konvo/AssistantConnector';

export type Method = ConnectorCapability['method'];
export type Place = 'path' | 'query' | 'body';

export const pathParameters = (path: string) => Array.from(path.matchAll(/\{([a-z][a-zA-Z0-9_]*)\}/g), (m) => m[1]);

/** A lowercase ID from a name, unique among `taken`: "Lore (prod)" → "lore_prod". */
export function connectorSlug(text: string, taken: ReadonlySet<string>) {
	let base = text
		.toLowerCase()
		.normalize('NFKD')
		.replace(/[^a-z0-9]+/g, '_')
		.replace(/^_+|_+$/g, '')
		.slice(0, 56);
	if (!/^[a-z]/.test(base)) base = base ? `api_${base}` : 'custom';
	let id = base;
	for (let n = 2; taken.has(id); n++) id = `${base}_${n}`;
	return id;
}

export function blankCustomConnector(): AssistantConnectorDefinition {
	return {
		id: '',
		name: '',
		description: '',
		provider: 'api',
		base_url: '',
		network: 'public',
		enabled: true,
		shared: false,
		auth: 'bearer',
		client_id: '',
		scopes: '',
		capabilities: [],
	};
}

export function blankAction(): ConnectorCapability {
	return {
		id: '',
		label: '',
		description: '',
		method: 'GET',
		path: '/',
		effect: 'read',
		parameters: [],
		scope: [],
		query: [],
		body: [],
	};
}

export function placeOf(action: ConnectorCapability, name: string): Place {
	if (pathParameters(action.path).includes(name)) return 'path';
	if (action.body.includes(name)) return 'body';
	if (action.query.includes(name)) return 'query';
	return action.method === 'GET' ? 'query' : 'body';
}

/**
 * Fills in what the rows leave implicit: IDs from labels, read/write from the method, every
 * path {placeholder} as a required parameter, query or body placement, and (for writes) path
 * parameters fixed at approval so an automation can't retarget them.
 */
export function normalizeAction(action: ConnectorCapability, taken: Set<string>): ConnectorCapability {
	const inPath = pathParameters(action.path);
	const parameters = [...action.parameters];
	for (const name of inPath)
		if (!parameters.some((p) => p.name === name)) parameters.push({name, label: name, type: 'string', required: true});
	const place = (p: ConnectorParameter) => {
		const where = placeOf(action, p.name);
		return where === 'body' && action.method === 'GET' ? 'query' : where;
	};
	const names = new Set(parameters.map((p) => p.name));
	const write = action.method !== 'GET';
	const id = action.id || connectorSlug(action.label || action.path, taken);
	taken.add(id);
	return {
		...action,
		id,
		label: action.label.trim() || id,
		effect: write ? 'write' : 'read',
		parameters: parameters.map((p) => (inPath.includes(p.name) ? {...p, required: true} : p)),
		query: parameters.filter((p) => place(p) === 'query').map((p) => p.name),
		body: parameters.filter((p) => place(p) === 'body').map((p) => p.name),
		scope: write ? Array.from(new Set([...action.scope.filter((n) => names.has(n)), ...inPath])) : [],
	};
}

export function normalizeConnector(
	d: AssistantConnectorDefinition,
	taken: ReadonlySet<string>,
): AssistantConnectorDefinition {
	const ids = new Set<string>();
	return {
		...d,
		id: d.id || connectorSlug(d.name, taken),
		name: d.name.trim(),
		description: d.description.trim(),
		base_url: d.base_url.trim().replace(/\/+$/, ''),
		network: d.provider === 'api' ? d.network : 'public',
		capabilities: d.capabilities.map((c) => normalizeAction(c, ids)),
	};
}
